import { isHangingIndent, referencesTitle } from "@vericus/cadmus-common";
import {
  CadmusEditorOptions,
  CoreEditor,
  createCadmusEditor,
  Editor,
  getVersion,
  JSONContent,
  Sendable,
  sendableSteps,
  sendableWordCountChange,
} from "@vericus/cadmus-editor-prosemirror";

import { trackEditorEvent } from "@/client/events";
import {
  __GLOBAL_ASSESSMENT_ID,
  __GLOBAL_CLIENT_ID,
  __GLOBAL_STUDENT_ID,
  __GLOBAL_TENANT,
  isCadmusTenant,
} from "@/client/globals";
import GLOBAL_SUGGESTION_CLIENT from "@/client/suggestion";
import { API_ENDPOINT } from "@/config";
import { AppDispatch } from "@/data/store";
import {
  SavingMechanism,
  setDocumentError,
  setPendingSnapshot,
  StreamConnection,
} from "@/features/authority";
import { time } from "@/utils/serverTime";

import { Snapshot } from "../snapshot";
import { getStudentChannel } from "../student-channel";
import { setActiveEditor } from "./active-editor";
import defaultNotesExam from "./default-notes-exam.json";
import defaultNotes from "./default-notes.json";
import {
  ClassicEditorName,
  classicEditorNameToEditorName,
  EditorName,
  ExtendedQuestionEditorName,
  extendedQuestionEditorNameToEditorName,
} from "./types";

interface NewAnswerEditorArgs {
  /** Answer Block that the editor is connected to. */
  answerBlockId: string;
  /** Name of the editor if it is one. */
  editorName: EditorName | null;
  /** Initial content. */
  content: JSONContent | null;
  /** Initial content's version */
  version: number;
  /** Redux dispatcher */
  dispatch: AppDispatch;
  /** Work's assessment settings from the Aphrodite context. */
  workContext: WorkContext;
  /** Extra override Cadmus Editor initialisation options. */
  editorOpts?: Partial<CadmusEditorOptions>;
}

/**
 * Contextual information about the Work being edited.
 */
export interface WorkContext {
  workId: string;
  isCollaborative: boolean;
  savingMechanism: SavingMechanism;
  isExam: boolean;
  referencingStyle: string | null;
  isMultiFormat: boolean;
  isLockDownExam: boolean;
  assessmentName: string;
  spellCheckDisabled: boolean;
}

/**
 * Create a new Editor from a snapshot or empty state and optionally connect it
 * to a collaborative Authority Stream.
 *
 * @param args - Editor initialisation args
 * @returns a Promise resolving to the initialised Editor with its contents.
 *      Wait on this promise before allowing the user to make edits to the
 *      Editor.
 */
async function newAnswerEditor(args: NewAnswerEditorArgs): Promise<Editor> {
  const {
    answerBlockId,
    version,
    editorName,
    content,
    dispatch,
    workContext,
    editorOpts,
  } = args;

  const studentChannel = getStudentChannel({
    channelType: "work",
    workId: workContext.workId,
  });

  // Traditional saving without collaboration
  const onNonCollabSave = (editor: CoreEditor) => {
    const sendable = sendableSteps(editor.state);
    if (sendable) {
      const sendableChange = sendableWordCountChange(sendable);
      selfConfirmSteps(editor, sendable);
      const snapshot = {
        version: getVersion(editor.state),
        answerDoc: editor.getJSON(),
        wordCountChange: sendableChange,
      };
      const payload = { answerBlockId, snapshot, updateLocalSnapshot: true };
      // And queue it for saving
      dispatch(setPendingSnapshot(payload));
    }
  };

  // TODO: Add a streamUUID edge to AnswerBlock type indicating that the
  // AnswerBlock is meant to collaborate using a specific Authority Stream.
  // Will be resolved in CDMS-2503.
  const streamId = answerBlockId;

  // default contents for the editor
  const initialContent = defaultContent(editorName, workContext);

  const editor = createCadmusEditor({
    editorId: editorName ?? answerBlockId,
    editorA11yLabel: "Student answer",
    editable: true,
    content: version === 0 ? initialContent : content,
    version,
    clientId: __GLOBAL_CLIENT_ID.current,
    suggestionClient: GLOBAL_SUGGESTION_CLIENT,
    imageAPIBase: `${API_ENDPOINT}/api/work_media`,
    tenant: __GLOBAL_TENANT.current ?? undefined,
    assessmentId: __GLOBAL_ASSESSMENT_ID.current ?? undefined,
    workId: workContext.workId,
    userId: __GLOBAL_STUDENT_ID.current ?? undefined,
    userRole: "student",
    contentPlaceholder: "Write your answer here…",
    autofocus: true,
    enablePasteAnnotations: true,
    onEditorEvent: trackEditorEvent,
    cursorBottomPadding: 144,
    onSave: workContext.isCollaborative
      ? undefined
      : (_editorId, editor) => onNonCollabSave(editor),
    getTime: () => new Date(time()).toISOString(),
    collabPresence:
      studentChannel !== null ? studentChannel.collabPresence : undefined,
    ...editorOpts,
  });

  editor.on("focus", () => {
    setActiveEditor(answerBlockId, editor, editorName);
  });

  if (workContext.isCollaborative) {
    const authorityConn = new StreamConnection({
      editor,
      streamId,
      tenant: __GLOBAL_TENANT.current ?? "",
      clientId: __GLOBAL_CLIENT_ID.current ?? "",
      initialState: initialContent,
      studentChannel: studentChannel ?? undefined,
      onFailure: () => {
        dispatch(setDocumentError());
      },
      onSnapshot: ({ doc, version, wordCountChange }) => {
        dispatch(
          setPendingSnapshot({
            answerBlockId,
            snapshot: { version, answerDoc: doc, wordCountChange },
            updateLocalSnapshot: true,
          })
        );
      },
    });

    await authorityConn.connect();
  }
  return editor;
}

// Auto confirm steps
function selfConfirmSteps(editor: CoreEditor, sendable: Sendable) {
  const clientIds = sendable.steps.map(() => sendable.clientID);
  editor.commands.receiveSteps(sendable.steps, clientIds);
}

/**
 * Create a Editor from snapshots of extended questions.
 */
export async function newExtendedQuestionEditor(
  editorName: ExtendedQuestionEditorName,
  answerBlockId: string,
  snapshot: Snapshot | null,
  dispatch: AppDispatch,
  workContext: WorkContext
): Promise<Editor> {
  return await newAnswerEditor({
    editorName: extendedQuestionEditorNameToEditorName(editorName),
    answerBlockId,
    content: snapshot?.answerDoc ?? null,
    version: snapshot?.version ?? 0,
    dispatch,
    editorOpts: buildExtendedQuestionEditorOpts(editorName, workContext),
    workContext,
  });
}

function buildExtendedQuestionEditorOpts(
  editorName: ExtendedQuestionEditorName,
  workContext: WorkContext
): Partial<CadmusEditorOptions> {
  const { isLockDownExam, referencingStyle } = workContext;

  const enableHangingIndent = isHangingIndent(referencingStyle);

  const commonOpts: Partial<CadmusEditorOptions> = {
    enableHyperlink: !isLockDownExam,
    enableImage: !isLockDownExam,
    enableAudio: false,
    enableVideo: false,
  };

  switch (editorName) {
    case ExtendedQuestionEditorName.Body:
      return {
        ...commonOpts,
        enableFootnoting: isCadmusTenant(),
      };
    case ExtendedQuestionEditorName.References:
      return {
        ...commonOpts,
        enableHangingIndent,
        editorA11yLabel: "Question references editor",
        contentPlaceholder: "References...",
        referencesPlaceholder: true,
        autofocus: false,
      };

    default:
      return commonOpts;
  }
}

/**
 * Create a Editor from snapshots of classic editor.
 */
export async function newClassicEditor(
  editorName: ClassicEditorName,
  answerBlockId: string,
  snapshot: Snapshot | null,
  dispatch: AppDispatch,
  workContext: WorkContext
): Promise<Editor> {
  return await newAnswerEditor({
    editorName: classicEditorNameToEditorName(editorName),
    answerBlockId,
    content: snapshot?.answerDoc ?? null,
    version: snapshot?.version ?? 0,
    dispatch,
    editorOpts: buildClassicEditorOpts(editorName, workContext),
    workContext,
  });
}

function buildClassicEditorOpts(
  editorName: ClassicEditorName,
  workContext: WorkContext
): Partial<CadmusEditorOptions> {
  const { isExam, isLockDownExam, referencingStyle } = workContext;

  const enableHangingIndent = isHangingIndent(referencingStyle);

  const commonOpts: Partial<CadmusEditorOptions> = {
    onEditorEvent: trackEditorEvent,
    enableManual: !isExam,
    enableHyperlink: !isLockDownExam,
    enableImage: !isLockDownExam,
    enableAudio: false,
    enableVideo: false,
    editable: true,
    disableCadmusSpellCheck: workContext.spellCheckDisabled,
  };

  switch (editorName) {
    case ClassicEditorName.Body:
      return {
        ...commonOpts,
        editorA11yLabel: "Assessment body editor",
        enablePasteAnnotations: true,
        titlePlaceholder: "Give your work a title",
        contentPlaceholder: "Write your submission here...",
        autofocus: false,
        minBlocks: 2,
        enableFootnoting: true,
        enableCommenting: true,
      };
    case ClassicEditorName.Notes:
      return {
        ...commonOpts,
        editorA11yLabel: "Assessment notes editor",
        titlePlaceholder: "Notes...",
        contentPlaceholder:
          "Use this space to plan, take notes, or work through your checklist. Your notes won’t be submitted with your work.",
        autofocus: false,
      };
    case ClassicEditorName.References:
      return {
        ...commonOpts,
        editorA11yLabel: "Assessment references editor",
        titlePlaceholder: "References...",
        referencesPlaceholder: true,
        enableHangingIndent: enableHangingIndent ?? false,
        autofocus: false,
      };

    default:
      return commonOpts;
  }
}

const defaultEditorContent = { type: "doc", content: [{ type: "paragraph" }] };

function defaultContent(
  editorName: EditorName | null,
  workContext: WorkContext
): JSONContent {
  switch (editorName) {
    case EditorName.Body:
      return workContext.isMultiFormat
        ? defaultEditorContent
        : defaultBodyContent(workContext.assessmentName);
    case EditorName.Notes:
      return defaultNotesContent(workContext.isExam);
    case EditorName.References:
      return defaultReferencesContent(workContext.referencingStyle);
    default:
      return defaultEditorContent;
  }
}

function defaultBodyContent(assessmentName: string): JSONContent {
  return {
    type: "doc",
    content: [
      {
        type: "heading",
        attrs: { level: 1 },
        content: [{ type: "text", text: assessmentName }],
      },
      {
        type: "paragraph",
      },
    ],
  };
}

function defaultNotesContent(isExam: boolean): JSONContent {
  return isExam ? defaultNotesExam : defaultNotes;
}

function defaultReferencesContent(
  referencingStyle: string | null
): JSONContent {
  return {
    type: "doc",
    content: [
      {
        type: "heading",
        attrs: { level: 1 },
        content: [{ type: "text", text: referencesTitle(referencingStyle) }],
      },
      { type: "paragraph" },
    ],
  };
}
